import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import * as _ from 'lodash-es';
import moment from 'moment';
import {combineLatest, Observable} from 'rxjs';
import {catchError, map, retry} from 'rxjs/operators';
import {createHttpParamsByListFilter, handleResponseError} from '../../../common/api-helpers';
import {toApiDateTime} from '../../../common/dates';
import {retryStrategyWithIncreasedDelay} from '../../../common/rxjs/retry-strategy-with-increased-delay';
import {SecurityService} from '../../../components/security';
import {isAsecurityKey} from '../../../components/security/aspreader-security';
import {CANDLE_PERIOD_MOMENT_DURATION_DICT, CandlePeriod} from '../../../enums/candle-period';
import {ListFilterFactory} from '../../../factories/list-filter.factory';
import {Candle} from '../../../models/candle';
import {CommonListFilter} from '../../../models/filters/common-list-filter';
import {FilterCriterion} from '../../../models/filters/filter-criterion';
import {QueryParamFilterCriterion} from '../../../models/filters/query-param-filter-criterion';
import {clearSecurityKey, SecurityKey} from '../../../models/security-key';
import {AppConfigService} from '../../../services/config.service';
import {
    createAsecurityCandlesRequest,
    getSecurityKeyCandleFilterCriterion,
    getSecurityKeyFromCandleFilters
} from './asecurity-candle-utils';
import {CandlesResponse} from './candles-rest-api-response';

const MAX_CANDLE_COUNT = 1500;

interface TimeRangeInterval {
    dateFrom: Date;
    dateTo: Date;
}

@Injectable()
export class CandleService {

    private candlesUrl: string = this.appConfigService.api['candles'];

    constructor(
        private httpClient: HttpClient,
        private appConfigService: AppConfigService,
        private securityService: SecurityService
    ) {}

    public getCandles(listFilter: CommonListFilter): Observable<CandlesResponse> {
        const getCandlesHistoryUrl: string = `${this.candlesUrl}/history`;

        const commonSecurityRequest = (partListFilter: CommonListFilter): Observable<CandlesResponse> =>
            this.httpClient.get<CandlesResponse>(getCandlesHistoryUrl, {
                params: createHttpParamsByListFilter(partListFilter)
            }).pipe(
                retry({delay: retryStrategyWithIncreasedDelay()}),
                catchError(handleResponseError)
            );

        const securityKey: SecurityKey = getSecurityKeyFromCandleFilters(listFilter.filters);

        return isAsecurityKey(securityKey)
            ? createAsecurityCandlesRequest(securityKey, listFilter, commonSecurityRequest, this.securityService)
            : commonSecurityRequest(listFilter);
    }

    public getCandlesByParameters({securityKey, candlePeriod, dateFrom, dateTo}: {
        securityKey: SecurityKey;
        candlePeriod: CandlePeriod;
        dateFrom: Date;
        dateTo: Date | null;
    }): Observable<Candle[]> {
        const intervals = this.splitTimeRangeByCandlePeriod(candlePeriod, dateFrom, dateTo);

        const requests = intervals.map(interval => this.getCandlesByParametersOneRequest({
            securityKey: securityKey,
            candlePeriod,
            dateFrom:    interval.dateFrom,
            dateTo:      interval.dateTo
        }));

        return combineLatest(requests).pipe(
            map(responses => [].concat(...responses))
        );
    }

    /**
     * Creates a request for candles, not taking into account that candles limit can be overflown.
     * @param {SecurityKey} security
     * @param {CandlePeriod} candlePeriod
     * @param {Date} dateFrom
     * @param {Date} dateTo
     */
    private getCandlesByParametersOneRequest({securityKey, candlePeriod, dateFrom, dateTo}: {
        securityKey: SecurityKey;
        candlePeriod: CandlePeriod;
        dateFrom: Date;
        dateTo: Date;
    }): Observable<Candle[]> {
        const filterBySecurity = getSecurityKeyCandleFilterCriterion(securityKey);

        const filterByCandlePeriod = new QueryParamFilterCriterion({
            query:          candlePeriod,
            queryParamName: 'candlePeriod'
        });

        const filterByCount = new QueryParamFilterCriterion({
            query:          MAX_CANDLE_COUNT,
            queryParamName: 'count'
        });

        const filterByDateFrom = new QueryParamFilterCriterion({
            query:          toApiDateTime(dateFrom),
            queryParamName: 'from'
        });

        const filterByDateTo = dateTo && new QueryParamFilterCriterion({
            query:          toApiDateTime(dateTo),
            queryParamName: 'to'
        });

        const filterCriteria: FilterCriterion[] = [
            filterBySecurity,
            filterByCandlePeriod,
            filterByCount,
            filterByDateFrom,
            filterByDateTo
        ].filter(filter => !!filter);

        const candlesFilter = ListFilterFactory.createWithSearchFields(filterCriteria);

        candlesFilter.pageCount = null;
        candlesFilter.pageSize = null;

        return this.getCandles(candlesFilter).pipe(
            map(candlesResponse => {
                return candlesResponse.candles.map(candleData => {
                    const [id, openPrice, highPrice, lowPrice, closePrice, volume, closeTime] = candleData;

                    return {
                        id,
                        openPrice,
                        highPrice,
                        lowPrice,
                        closePrice,
                        volume,
                        closeTime,
                        candlePeriod: candlesResponse.candlePeriod,
                        securityKey:  clearSecurityKey(candlesResponse.security),
                        security:     candlesResponse.security
                    };
                });
            })
        );
    }

    /**
     * Splits the passed time range to intervals according to {@link MAX_CANDLE_COUNT}
     * @param {CandlePeriod} candlePeriod
     * @param {Date} dateFrom
     * @param {Date} dateTo
     * @returns TimeRangeInterval[]
     */
    private splitTimeRangeByCandlePeriod(
        candlePeriod: CandlePeriod,
        dateFrom: Date,
        dateTo: Date | null
    ): TimeRangeInterval[] {
        const ONE_SECOND = 1000;
        const dateFromMoment = moment(dateFrom);
        const msDateFrom = dateFromMoment.valueOf();
        const dateToMoment = moment(dateTo || undefined); // We need current time in case of null.
        const timeSpan = dateToMoment.diff(dateFromMoment);
        const candleDuration = CANDLE_PERIOD_MOMENT_DURATION_DICT[candlePeriod].asMilliseconds();
        const candleCountInTimeRange = timeSpan / candleDuration;
        const intervalCount = Math.ceil(candleCountInTimeRange / MAX_CANDLE_COUNT);
        let intervals: TimeRangeInterval[];

        if (intervalCount > 1) {

            // How many ms are contained in one interval.
            const intervalTimeSpan = candleDuration * MAX_CANDLE_COUNT;

            intervals = _.times(intervalCount, iInterval => ({
                dateFrom: moment(msDateFrom + iInterval * intervalTimeSpan).toDate(),
                dateTo: moment(msDateFrom + (iInterval + 1) * intervalTimeSpan - ONE_SECOND).toDate()
            }));

            intervals[intervals.length - 1].dateTo = dateTo;
        } else {
            intervals = [{dateFrom, dateTo}];
        }

        return intervals;
    }

}
